import { fetchProductSKUs, productSKUWithID, getAllProductSKUs, getFirstLiveSKUFromOrdering } from "@shared/productSKUFeed.js";
import { fetchColors, getOverlayImageID } from "@shared/colorFeed.js";
import { fetchRenderSpecs } from "@shared/renderSpecFeed.js";
import { fetchConfig, getProductOrdering } from "@shared/configFeed.js";
import { fetchStickerSets, stickerSetWithID } from "@shared/stickerSetFeed.js";
import * as stickerSetFeed from "@shared/stickerSetFeed.js";
import { fetchGalleryImageFeed, galleryImageWithID } from "@shared/galleryImageFeed.js";
import { CYODesign } from "@shared/CYODesign.js";
import { Compositor } from "@shared/Compositor.js";
import { Product } from "@shared/Product.js";
import imageStorage from "@shared/ImageStorage.js";
import { cfProcessImage, convertImageToFile, loadImage } from "@shared/imgUtil.js";
import { fetchCFImage } from "@shared/cloudflare.js";
import { CartUtils } from "@shared/CartUtils.js";
import { initFirebase } from "@shared/firebase.js";
import { compareCanvasToReference } from "./pixelDiff.js";

var testUITransparentUrl = "/test-ui-transparent.png";
var testRectUrl = "/test-rect.png";
var testGIUrl = "/test-gi.png";

var STORAGE_KEY_CDN_URL = "print-preview-test-cdn-url";
var FIXED_RANDOM_SEED = 42;
var FORCED_DPR = 1;

// Module references - can be swapped when loading from CDN
var mod = {
    initFirebase: initFirebase,
    fetchProductSKUs: fetchProductSKUs,
    productSKUWithID: productSKUWithID,
    getAllProductSKUs: getAllProductSKUs,
    getFirstLiveSKUFromOrdering: getFirstLiveSKUFromOrdering,
    fetchColors: fetchColors,
    getOverlayImageID: getOverlayImageID,
    fetchRenderSpecs: fetchRenderSpecs,
    fetchConfig: fetchConfig,
    getProductOrdering: getProductOrdering,
    fetchStickerSets: fetchStickerSets,
    stickerSetWithID: stickerSetWithID,
    stickerSetFeed: stickerSetFeed,
    fetchGalleryImageFeed: fetchGalleryImageFeed,
    galleryImageWithID: galleryImageWithID,
    CYODesign: CYODesign,
    Compositor: Compositor,
    Product: Product,
    imageStorage: imageStorage,
    cfProcessImage: cfProcessImage,
    convertImageToFile: convertImageToFile,
    loadImage: loadImage,
    fetchCFImage: fetchCFImage,
    CartUtils: CartUtils
};

// Test case definitions
var GRIP_DEVICE_SKU = "701453";
var CASE_DEVICE_SKU = "706391";
var WALLET_DEVICE_SKU = "805777";
var STICKER_SET_ID = "sticker-set-debug-xAtbPX593ftK";

var GRIP_TEST_CASES = [
    {

        label: "Fun stuff sticker only (border)",
        stickers: true,
        galleryBG: false,
        userImage: false,
        description: "Stickers: border placement"
    },
    {

        label: "Fun stuff sticker only (pattern)",
        stickers: true,
        galleryBG: false,
        userImage: false,
        stickerPlacement: "pattern",
        description: "Stickers: pattern placement"
    },
    {

        label: "Fun stuff image only",
        stickers: false,
        galleryBG: true,
        userImage: false,
        description: ""
    },
    {

        label: "User image only",
        stickers: false,
        galleryBG: false,
        userImage: true,
        description: "User: bottom, remove-bg"
    },
    {

        label: "User image + fun stuff sticker",
        stickers: true,
        galleryBG: false,
        userImage: true,
        description: "Stickers: border; User: bottom, remove-bg"
    },
    {
        label: "User image (full) + fun stuff sticker",
        stickers: true,
        galleryBG: false,
        userImage: true,
        userImageKey: "testRect",
        subjectStyle: "full",
        subjectPlacement: "center",
        description: "Stickers: border; User: full (test-rect.png)"
    },
    {

        label: "User image + fun stuff image",
        stickers: false,
        galleryBG: true,
        userImage: true,
        description: "User: bottom, remove-bg"
    },
    {

        label: "User image (full)",
        stickers: false,
        galleryBG: false,
        userImage: true,
        userImageKey: "testRect",
        subjectStyle: "full",
        subjectPlacement: "center",
        description: "User: full image style (test-rect.png)"
    }
];

var CASE_TEST_CASES = [
    {

        label: "Fun stuff sticker only (border)",
        stickers: true,
        galleryBG: false,
        userImage: false,
        description: "Stickers: border placement"
    },
    {

        label: "Fun stuff sticker only (pattern)",
        stickers: true,
        galleryBG: false,
        userImage: false,
        stickerPlacement: "pattern",
        description: "Stickers: pattern placement"
    },
    {

        label: "Fun stuff image only",
        stickers: false,
        galleryBG: true,
        userImage: false,
        description: ""
    },
    {

        label: "User image only",
        stickers: false,
        galleryBG: false,
        userImage: true,
        description: "User: bottom, remove-bg"
    },
    {

        label: "User image + fun stuff sticker",
        stickers: true,
        galleryBG: false,
        userImage: true,
        description: "Stickers: border; User: bottom, remove-bg"
    },
    {
        label: "User image (full) + fun stuff sticker",
        stickers: true,
        galleryBG: false,
        userImage: true,
        userImageKey: "testRect",
        subjectStyle: "full",
        subjectPlacement: "center",
        description: "Stickers: border; User: full (test-rect.png)"
    },
    {

        label: "User image + fun stuff image",
        stickers: false,
        galleryBG: true,
        userImage: true,
        description: "User: bottom, remove-bg"
    },
    {

        label: "User image (full)",
        stickers: false,
        galleryBG: false,
        userImage: true,
        subjectStyle: "full",
        subjectPlacement: "center",
        description: "User: full image style"
    },
    {
        label: "User image (full) + stickers (corner)",
        stickers: true,
        galleryBG: false,
        userImage: true,
        userImageKey: "testRect",
        subjectStyle: "full",
        subjectPlacement: "center",
        stickerPlacement: "corner",
        description: "Stickers: corner; User: full (test-rect.png)"
    }
];

var WALLET_TEST_CASES = [
    {
        label: "Fun stuff stickers only",
        stickers: true,
        galleryBG: false,
        userImage: false,
        mode: "split",
        description: "Stickers: border"
    },
    {
        label: "Fun stuff stickers only",
        stickers: true,
        galleryBG: false,
        userImage: false,
        mode: "both",
        description: "Stickers: border"
    },
    {
        label: "Fun stuff stickers only",
        stickers: true,
        galleryBG: false,
        userImage: false,
        mode: "grip",
        description: "Stickers: border"
    },
    {
        label: "Fun stuff image",
        stickers: false,
        galleryBG: true,
        userImage: false,
        mode: "split",
        description: ""
    },
    {
        label: "User image",
        stickers: false,
        galleryBG: false,
        userImage: true,
        mode: "split",
        description: "User: bottom, remove-bg"
    },
    {
        label: "User image",
        stickers: false,
        galleryBG: false,
        userImage: true,
        mode: "both",
        description: "User: bottom, remove-bg"
    },
    {
        label: "User image",
        stickers: false,
        galleryBG: false,
        userImage: true,
        mode: "wallet",
        description: "User: bottom, remove-bg"
    },
    {
        label: "User image",
        stickers: false,
        galleryBG: false,
        userImage: true,
        mode: "grip",
        description: "User: bottom, remove-bg"
    },
    {
        label: "Stickers + user image",
        stickers: true,
        galleryBG: false,
        userImage: true,
        mode: "split",
        description: "Stickers: border; User: bottom, remove-bg"
    },
    {
        label: "Stickers + user image",
        stickers: true,
        galleryBG: false,
        userImage: true,
        mode: "both",
        description: "Stickers: border; User: bottom, remove-bg"
    },
    {
        label: "Stickers + user image",
        stickers: true,
        galleryBG: false,
        userImage: true,
        mode: "wallet",
        description: "Stickers: border; User: bottom, remove-bg"
    },
    {
        label: "Stickers + user image",
        stickers: true,
        galleryBG: false,
        userImage: true,
        mode: "grip",
        description: "Stickers: border; User: bottom, remove-bg"
    },
    {
        label: "Stickers + user image (full)",
        stickers: true,
        galleryBG: false,
        userImage: true,
        userImageKey: "testRect",
        subjectStyle: "full",
        subjectPlacement: "center",
        mode: "split",
        description: "Stickers: border; User: full (test-rect.png)"
    },
    {
        label: "Stickers + user image (full)",
        stickers: true,
        galleryBG: false,
        userImage: true,
        userImageKey: "testRect",
        subjectStyle: "full",
        subjectPlacement: "center",
        mode: "both",
        description: "Stickers: border; User: full (test-rect.png)"
    },
    {
        label: "Stickers + user image (full)",
        stickers: true,
        galleryBG: false,
        userImage: true,
        userImageKey: "testRect",
        subjectStyle: "full",
        subjectPlacement: "center",
        mode: "wallet",
        description: "Stickers: border; User: full (test-rect.png)"
    },
    {
        label: "Stickers + user image (full)",
        stickers: true,
        galleryBG: false,
        userImage: true,
        userImageKey: "testRect",
        subjectStyle: "full",
        subjectPlacement: "center",
        mode: "grip",
        description: "Stickers: border; User: full (test-rect.png)"
    },
    {
        label: "User image full",
        stickers: false,
        galleryBG: false,
        userImage: true,
        subjectStyle: "full",
        subjectPlacement: "center",
        mode: "split",
        description: "User: full image style"
    },
    {
        label: "User image full",
        stickers: false,
        galleryBG: false,
        userImage: true,
        subjectStyle: "full",
        subjectPlacement: "center",
        mode: "both",
        description: "User: full image style"
    },
    {
        label: "User image full",
        stickers: false,
        galleryBG: false,
        userImage: true,
        subjectStyle: "full",
        subjectPlacement: "center",
        mode: "wallet",
        description: "User: full image style"
    },
    {
        label: "User image full",
        stickers: false,
        galleryBG: false,
        userImage: true,
        subjectStyle: "full",
        subjectPlacement: "center",
        mode: "grip",
        description: "User: full image style"
    },
    {
        label: "User image (full) + stickers (corner)",
        stickers: true,
        galleryBG: false,
        userImage: true,
        userImageKey: "testRect",
        subjectStyle: "full",
        subjectPlacement: "center",
        stickerPlacement: "corner",
        mode: "split",
        description: "Stickers: corner; User: full (test-rect.png)"
    },
    {
        label: "User image (full) + stickers (corner)",
        stickers: true,
        galleryBG: false,
        userImage: true,
        userImageKey: "testRect",
        subjectStyle: "full",
        subjectPlacement: "center",
        stickerPlacement: "corner",
        mode: "both",
        description: "Stickers: corner; User: full (test-rect.png)"
    },
    {
        label: "User image (full) + stickers (corner)",
        stickers: true,
        galleryBG: false,
        userImage: true,
        userImageKey: "testRect",
        subjectStyle: "full",
        subjectPlacement: "center",
        stickerPlacement: "corner",
        mode: "wallet",
        description: "Stickers: corner; User: full (test-rect.png)"
    },
    {
        label: "User image (full) + stickers (corner)",
        stickers: true,
        galleryBG: false,
        userImage: true,
        userImageKey: "testRect",
        subjectStyle: "full",
        subjectPlacement: "center",
        stickerPlacement: "corner",
        mode: "grip",
        description: "Stickers: corner; User: full (test-rect.png)"
    }
];

// Cached stock assets
var stockAssets = {
    gripDevice: null,
    caseDevice: null,
    walletDevice: null,
    walletGripDevice: null,
    stickerSet: null,
    stickerImages: null,
    galleryBGImage: null,
    userImage: null,
    userTransparentImage: null,
    testRectImage: null
};

// Track which tabs have been rendered
var renderedTabs = {};
var activeTab = "grips";

function setStatus(msg, isError) {
    var el = document.getElementById("status-msg");
    el.textContent = msg;
    if (isError) {
        el.className = "error";
    } else {
        el.className = "";
    }
}

// CDN loading via fetch + blob URL
async function loadFromCDN(url) {
    setStatus("Fetching CDN module...");

    // Route poplabscdn.com through Vite dev proxy to avoid CORS
    var fetchUrl = url;
    try {
        var parsed = new URL(url);
        if (parsed.hostname === "poplabscdn.com") {
            fetchUrl = "/cdn-proxy" + parsed.pathname;
        }
    } catch (e) {
        // not a valid URL, try as-is
    }

    var response = await fetch(fetchUrl);
    if (!response.ok) {
        throw new Error("Failed to fetch: " + response.status);
    }
    var text = await response.text();
    var blob = new Blob([text], { type: "text/javascript" });
    var blobUrl = URL.createObjectURL(blob);

    setStatus("Importing CDN module...");
    var cdnModule = await import(/* @vite-ignore */ blobUrl);
    URL.revokeObjectURL(blobUrl);

    // Verify required exports
    var required = [
        "initFirebase",
        "fetchProductSKUs", "productSKUWithID", "getAllProductSKUs", "getFirstLiveSKUFromOrdering",
        "fetchColors", "getOverlayImageID",
        "fetchRenderSpecs", "fetchConfig", "getProductOrdering",
        "fetchStickerSets", "stickerSetWithID",
        "stickerSetFeed", "fetchGalleryImageFeed", "galleryImageWithID",
        "CYODesign", "Compositor", "Product", "imageStorage",
        "cfProcessImage", "convertImageToFile", "loadImage",
        "fetchCFImage", "CartUtils"
    ];
    var missing = [];
    for (var i = 0; i < required.length; i++) {
        if (typeof cdnModule[required[i]] === "undefined") {
            missing.push(required[i]);
        }
    }
    if (missing.length > 0) {
        throw new Error("CDN build missing exports: " + missing.join(", ") + ". Needs a build with the new exports.");
    }

    // Replace module references
    mod = {};
    for (var j = 0; j < required.length; j++) {
        mod[required[j]] = cdnModule[required[j]];
    }

    setStatus("CDN module loaded successfully.");
}

async function initFeeds() {
    setStatus("Initializing Firebase...");
    mod.initFirebase();
    setStatus("Loading feeds...");
    await Promise.all([
        mod.fetchProductSKUs(),
        mod.fetchColors(),
        mod.fetchRenderSpecs(),
        mod.fetchConfig(),
        mod.fetchStickerSets(false, true),
        mod.fetchGalleryImageFeed()
    ]);
    setStatus("Feeds loaded.");
}

async function loadStockAssets() {
    setStatus("Loading stock assets...");

    // Load grip device
    stockAssets.gripDevice = mod.productSKUWithID(GRIP_DEVICE_SKU);
    if (!stockAssets.gripDevice) {
        var allDevices = mod.getAllProductSKUs();
        console.log("Grip device #" + GRIP_DEVICE_SKU + " not found. " + allDevices.length + " devices available.");
        stockAssets.gripDevice = allDevices.find(function(d) { return d.productCategory === "grip"; });
    }
    if (!stockAssets.gripDevice) {
        console.warn("No grip device found in feed");
    } else {
        console.log("Grip device: " + stockAssets.gripDevice.id + " - " + stockAssets.gripDevice.title);
    }

    // Load case device
    stockAssets.caseDevice = mod.productSKUWithID(CASE_DEVICE_SKU);
    if (!stockAssets.caseDevice) {
        var allDevices2 = mod.getAllProductSKUs();
        console.log("Case device #" + CASE_DEVICE_SKU + " not found. " + allDevices2.length + " devices available.");
        stockAssets.caseDevice = allDevices2.find(function(d) { return d.productCategory === "case"; });
    }
    if (!stockAssets.caseDevice) {
        console.warn("No case device found in feed");
    } else {
        console.log("Case device: " + stockAssets.caseDevice.id + " - " + stockAssets.caseDevice.title);
    }

    // Load wallet device
    stockAssets.walletDevice = mod.productSKUWithID(WALLET_DEVICE_SKU);
    if (!stockAssets.walletDevice) {
        var allDevices3 = mod.getAllProductSKUs();
        console.log("Wallet device #" + WALLET_DEVICE_SKU + " not found. " + allDevices3.length + " devices available.");
        stockAssets.walletDevice = allDevices3.find(function(d) { return d.productCategory === "wallet"; });
    }
    if (!stockAssets.walletDevice) {
        console.warn("No wallet device found in feed");
    } else {
        console.log("Wallet device: " + stockAssets.walletDevice.id + " - " + stockAssets.walletDevice.title);
    }

    // Load wallet grip device (grip-poptop, same as real app uses for wallet grip prints)
    var gripOrdering = mod.getProductOrdering("grip-poptop");
    stockAssets.walletGripDevice = mod.getFirstLiveSKUFromOrdering(gripOrdering);
    if (!stockAssets.walletGripDevice) {
        console.warn("No grip-poptop device found in config ordering, falling back to grip device");
        stockAssets.walletGripDevice = stockAssets.gripDevice;
    } else {
        console.log("Wallet grip device: " + stockAssets.walletGripDevice.id + " - " + stockAssets.walletGripDevice.title);
    }

    if (!stockAssets.gripDevice && !stockAssets.caseDevice && !stockAssets.walletDevice) {
        throw new Error("No devices found in feed");
    }

    // Load sticker set
    stockAssets.stickerSet = mod.stickerSetWithID(STICKER_SET_ID);
    if (!stockAssets.stickerSet) {
        throw new Error("Sticker set " + STICKER_SET_ID + " not found. Make sure debug sticker sets are loaded.");
    }

    // Load sticker images
    setStatus("Loading sticker images...");
    try {
        stockAssets.stickerImages = await mod.stickerSetFeed.imageArrayForSetWithID(STICKER_SET_ID);
    } catch (e) {
        throw new Error("Failed to load sticker images: " + (e.message || e));
    }

    // Load gallery BG image from local test asset
    setStatus("Loading gallery background...");
    stockAssets.galleryBGImage = await new Promise(function(resolve, reject) {
        var img = new Image();
        img.onload = function() { resolve(img); };
        img.onerror = function() { reject(new Error("Failed to load test-gi.png")); };
        img.src = testGIUrl;
    });

    // Load test-ui-transparent.png (used as both uploaded and transparent user image)
    setStatus("Loading user image...");
    var userImg = await new Promise(function(resolve, reject) {
        var img = new Image();
        img.onload = function() { resolve(img); };
        img.onerror = function() { reject(new Error("Failed to load test-ui-transparent.png")); };
        img.src = testUITransparentUrl;
    });
    stockAssets.userImage = userImg;
    stockAssets.userTransparentImage = userImg;

    // Load test-rect.png (for full image style tests)
    setStatus("Loading test-rect image...");
    stockAssets.testRectImage = await new Promise(function(resolve, reject) {
        var img = new Image();
        img.onload = function() { resolve(img); };
        img.onerror = function() { reject(new Error("Failed to load test-rect.png")); };
        img.src = testRectUrl;
    });

    setStatus("Assets loaded.");
}

function buildCYODesign(testCase) {
    var cyoDesign = new mod.CYODesign();

    if (testCase.stickers) {
        cyoDesign.stickerSet = stockAssets.stickerSet;
        cyoDesign.stickerPlacement = testCase.stickerPlacement || "border";
    } else {
        cyoDesign.stickerSet = null;
    }

    cyoDesign.remixSeed = FIXED_RANDOM_SEED;
    cyoDesign.debugOptions = cyoDesign.debugOptions || {};
    cyoDesign.debugOptions.disableRotations = true;

    if (testCase.galleryBG) {
        cyoDesign.backgroundImage = stockAssets.galleryBGImage;
    }

    if (testCase.userImage) {
        var img = testCase.userImageKey === "testRect" ? stockAssets.testRectImage : stockAssets.userImage;
        cyoDesign.userImage = img;
        cyoDesign.transparentImage = testCase.userImageKey === "testRect" ? img : stockAssets.userTransparentImage;
        cyoDesign.subjectStyle = testCase.subjectStyle || "remove-background";
        // "full" style must always use "center" placement
        cyoDesign.subjectPlacement = cyoDesign.subjectStyle === "full" ? "center" : (testCase.subjectPlacement || "bottom");
    }

    return cyoDesign;
}

var REFERENCE_DIR = "/reference-bitmaps/";

// Cache-bust param to bypass stale Cloudflare edge cache entries from a previous immutable cache policy. Cache-Control is now set to no-cache (see public/_headers) so it's no longer necessary to increment this going forward. (And after a year when the previous policy expires this can be removed completely...)
var REF_CACHE_VERSION = 1;

function loadReferenceImage(filename) {
    return new Promise(function(resolve) {
        var img = new Image();
        img.onload = function() { resolve(img); };
        img.onerror = function() { resolve(null); };
        img.src = REFERENCE_DIR + filename + "?v=" + REF_CACHE_VERSION;
    });
}

var PRDCAT_MAP = { "grips": "grip", "cases": "case", "wallet": "wallet" };

function buildRefFilename(prdcat, testCase, canvasVariant) {
    var parts = ["prdcat=" + prdcat];
    if (testCase.mode) {
        parts.push("sm=" + testCase.mode);
    }
    if (testCase.stickers) {
        parts.push("st-pl=" + (testCase.stickerPlacement || "border"));
    }
    if (testCase.galleryBG) {
        parts.push("gi");
    }
    if (testCase.userImage) {
        var sy = testCase.subjectStyle || "remove-background";
        var pl = sy === "full" ? "center" : (testCase.subjectPlacement || "bottom");
        var syAbbrev = sy === "remove-background" ? "rmbg" : (sy === "snapshot" ? "snp" : sy);
        parts.push("ui-pl=" + pl);
        parts.push("ui-sy=" + syAbbrev);
    }
    if (canvasVariant) {
        parts.push("cv=" + canvasVariant);
    }
    return parts.join(",") + ".png";
}

var testSummary = { passed: 0, failed: 0, noRef: 0, total: 0 };

function showComparisonResult(wrapper, result, maxWidth) {
    maxWidth = maxWidth || "400px";
    var badge = document.createElement("div");
    badge.className = "comparison-badge " + (result.pass ? "pass" : "fail");
    badge.textContent = result.pass
        ? "PASS"
        : "FAIL (" + result.diffPercent + "%)";
    wrapper.appendChild(badge);

    if (!result.pass) {
        var diffTitle = document.createElement("div");
        diffTitle.className = "canvas-title";
        diffTitle.textContent = "Diff";
        wrapper.appendChild(diffTitle);

        result.diffCanvas.style.maxWidth = maxWidth;
        result.diffCanvas.style.height = "auto";
        result.diffCanvas.style.display = "block";
        result.diffCanvas.style.border = "1px solid #d32f2f";
        wrapper.appendChild(result.diffCanvas);
    }
}

function showNoReference(wrapper) {
    var badge = document.createElement("div");
    badge.className = "comparison-badge no-ref";
    badge.textContent = "NO REF";
    wrapper.appendChild(badge);
}

function updateTestRowStatus(row, results) {
    var hasFail = false;
    var hasNoRef = false;
    for (var i = 0; i < results.length; i++) {
        testSummary.total++;
        if (results[i].ref === null) {
            testSummary.noRef++;
            hasNoRef = true;
        } else if (results[i].result && !results[i].result.pass) {
            testSummary.failed++;
            hasFail = true;
        } else {
            testSummary.passed++;
        }
    }
    if (hasFail) {
        row.setAttribute("data-test-status", "fail");
    } else if (hasNoRef) {
        row.setAttribute("data-test-status", "no-reference");
    } else {
        row.setAttribute("data-test-status", "pass");
    }
}

function updateTestSummaryElement() {
    var el = document.getElementById("test-summary");
    if (!el) return;
    el.setAttribute("data-passed", testSummary.passed);
    el.setAttribute("data-failed", testSummary.failed);
    el.setAttribute("data-no-ref", testSummary.noRef);
    el.setAttribute("data-total", testSummary.total);

    document.getElementById("summary-passed").textContent = testSummary.passed;
    document.getElementById("summary-failed").textContent = testSummary.failed;
    document.getElementById("summary-no-ref").textContent = testSummary.noRef;
    document.getElementById("summary-total").textContent = testSummary.total;

    var badge = document.getElementById("summary-badge");
    var detailList = document.getElementById("failed-list");
    if (testSummary.failed > 0) {
        badge.className = "summary-status has-fail";
        badge.textContent = testSummary.failed + " test" + (testSummary.failed === 1 ? "" : "s") + " failed";
    } else {
        badge.className = "summary-status all-pass";
        badge.textContent = "All tests passed";
    }

    var html = "";
    var failedRows = document.querySelectorAll('[data-test-status="fail"]');
    for (var i = 0; i < failedRows.length; i++) {
        var nameEl = failedRows[i].querySelector(".test-label-name");
        if (nameEl) {
            html += "<div class=\"detail-fail\">" + nameEl.textContent + "</div>";
        }
    }
    var noRefRows = document.querySelectorAll('[data-test-status="no-reference"]');
    for (var j = 0; j < noRefRows.length; j++) {
        var nameEl2 = noRefRows[j].querySelector(".test-label-name");
        if (nameEl2) {
            html += "<div class=\"detail-no-ref\">" + nameEl2.textContent + "</div>";
        }
    }
    if (failedRows.length > 0 || noRefRows.length > 0) {
        html += "<a href=\"#\" id=\"save-refs-for-listed-btn\" class=\"save-refs-link\">Create reference bitmaps for these</a>";
    }
    detailList.innerHTML = html;

    var saveBtn = document.getElementById("save-refs-for-listed-btn");
    if (saveBtn) {
        saveBtn.addEventListener("click", async function(e) {
            e.preventDefault();
            var rows = document.querySelectorAll('[data-test-status="fail"], [data-test-status="no-reference"]');
            var delay = 100;
            for (var k = 0; k < rows.length; k++) {
                var canvases = rows[k].querySelectorAll("canvas[data-ref-name]");
                for (var m = 0; m < canvases.length; m++) {
                    downloadCanvasAsPNG(canvases[m], canvases[m].dataset.refName);
                    await new Promise(function(r) { setTimeout(r, delay); });
                }
            }
            setStatus("Reference bitmaps saved. Move downloaded PNGs to print-preview-test/public/reference-bitmaps/");
        });
    }
}

function downloadCanvasAsPNG(canvas, filename) {
    var halfWidth = Math.round(canvas.width / 2);
    var halfHeight = Math.round(canvas.height / 2);
    var halfCanvas = document.createElement("canvas");
    halfCanvas.width = halfWidth;
    halfCanvas.height = halfHeight;
    var halfCtx = halfCanvas.getContext("2d");
    halfCtx.drawImage(canvas, 0, 0, halfWidth, halfHeight);

    var link = document.createElement("a");
    link.download = filename;
    link.href = halfCanvas.toDataURL("image/png");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

async function renderTestCase(testCase, device, container, prefix) {
    var coloredOverlayImageID = mod.getOverlayImageID(device);

    // -- Preview Canvas --
    var previewWrapper = container.querySelector(".preview-container");
    previewWrapper.innerHTML = "";

    var previewDesign = buildCYODesign(testCase);
    previewDesign.stickerImages = testCase.stickers ? stockAssets.stickerImages : [];

    var compositor = await mod.Compositor.createInContainer(
        previewWrapper, device, previewDesign, null, device.imageID, coloredOverlayImageID
    );
    compositor.updateCompositeView(null);

    // Show preview dimensions
    var previewCanvas = previewWrapper.querySelector(".composite-canvas");
    var previewDims = container.querySelector(".preview-dims");
    if (previewCanvas && previewDims) {
        previewDims.textContent = previewCanvas.width + " x " + previewCanvas.height;
    }

    // -- Print Canvas --
    var printContainer = container.querySelector(".print-container");
    printContainer.innerHTML = "";

    // Clear imageStorage state before this test case
    mod.imageStorage.setUploadedImage(null);
    mod.imageStorage.setTransparentImage(null);

    // Set imageStorage for print
    if (testCase.userImage) {
        var printImg = testCase.userImageKey === "testRect" ? stockAssets.testRectImage : stockAssets.userImage;
        var printTransImg = testCase.userImageKey === "testRect" ? stockAssets.testRectImage : stockAssets.userTransparentImage;
        mod.imageStorage.setUploadedImage(printImg);
        mod.imageStorage.setTransparentImage(printTransImg);
    }

    mod.Product.device = device;

    var mockPreviewCanvas = { compositor: compositor };
    var printCanvas = await mod.CartUtils.generatePrintReadyCanvas(previewDesign, mockPreviewCanvas);

    var prdcat = PRDCAT_MAP[prefix] || prefix;
    var printRefName = buildRefFilename(prdcat, testCase);
    printCanvas.dataset.refName = printRefName;

    printCanvas.style.maxWidth = "400px";
    printCanvas.style.height = "auto";
    printCanvas.style.display = "block";
    printCanvas.style.border = "1px solid #ccc";
    printContainer.appendChild(printCanvas);

    // Show print dimensions
    var printDims = container.querySelector(".print-dims");
    if (printDims) {
        printDims.textContent = printCanvas.width + " x " + printCanvas.height;
    }

    // Clear imageStorage after this test case
    mod.imageStorage.setUploadedImage(null);
    mod.imageStorage.setTransparentImage(null);

    // -- Compare against reference bitmap --
    var wrappers = container.querySelectorAll(".canvas-wrapper");
    var printWrapper = wrappers[1];

    var printRef = await loadReferenceImage(printRefName);

    var printResult = null;
    if (printCanvas && printRef) {
        printResult = compareCanvasToReference(printCanvas, printRef, {});
        showComparisonResult(printWrapper, printResult);
    } else if (!printRef) {
        showNoReference(printWrapper);
    }

    updateTestRowStatus(container, [
        { ref: printRef, result: printResult }
    ]);
}

function createTestCaseRow(testCase, prefix) {
    var row = document.createElement("div");
    row.className = "test-row";
    row.id = prefix + "-test-row-" + testCase.index;

    var prdcat = PRDCAT_MAP[prefix] || prefix;
    var refName = buildRefFilename(prdcat, testCase).replace(".png", "");

    row.innerHTML =
        "<div class=\"test-label\">" +
            "<div class=\"test-label-name\">" + refName + "</div>" +
        "</div>" +
        "<div class=\"canvas-pair\">" +
            "<div class=\"canvas-wrapper\">" +
                "<div class=\"canvas-title\">Preview</div>" +
                "<div class=\"preview-container\">" +
                    "<div class=\"loading-placeholder\">Waiting...</div>" +
                "</div>" +
                "<div class=\"canvas-dims preview-dims\"></div>" +
            "</div>" +
            "<div class=\"canvas-wrapper\">" +
                "<div class=\"canvas-title\">Print</div>" +
                "<div class=\"print-container\">" +
                    "<div class=\"loading-placeholder\">Waiting...</div>" +
                "</div>" +
                "<div class=\"canvas-dims print-dims\"></div>" +
            "</div>" +
        "</div>";

    return row;
}

async function renderTestCasesForTab(tabName, testCases, device, containerId) {
    if (!device) {
        setStatus("No " + tabName + " device available.", true);
        return;
    }

    var container = document.getElementById(containerId);
    container.innerHTML = "";

    for (var i = 0; i < testCases.length; i++) {
        testCases[i].index = i;
        container.appendChild(createTestCaseRow(testCases[i], tabName));
    }

    for (var j = 0; j < testCases.length; j++) {
        var testCase = testCases[j];
        var row = document.getElementById(tabName + "-test-row-" + j);
        setStatus("Rendering " + tabName + " test " + (j + 1) + "/" + testCases.length + "...");
        try {
            await renderTestCase(testCase, device, row, tabName);
        } catch (err) {
            console.error("Error rendering " + tabName + " test case " + (j + 1) + ":", err);
            var printContainer = row.querySelector(".print-container");
            printContainer.innerHTML = "<div style=\"color: #d32f2f; padding: 10px;\">Error: " + err.message + "</div>";
        }
    }

    renderedTabs[tabName] = true;
    setStatus("All " + testCases.length + " " + tabName + " test cases rendered.");
}

async function renderActiveTab() {
    if (activeTab === "grips") {
        await renderTestCasesForTab("grips", GRIP_TEST_CASES, stockAssets.gripDevice, "grip-test-cases-container");
    } else if (activeTab === "cases") {
        await renderTestCasesForTab("cases", CASE_TEST_CASES, stockAssets.caseDevice, "case-test-cases-container");
    } else if (activeTab === "wallet") {
        await renderWalletTests();
    }
}

async function renderAllTabs() {
    renderedTabs = {};
    testSummary = { passed: 0, failed: 0, noRef: 0, total: 0 };
    await renderTestCasesForTab("grips", GRIP_TEST_CASES, stockAssets.gripDevice, "grip-test-cases-container");
    await renderTestCasesForTab("cases", CASE_TEST_CASES, stockAssets.caseDevice, "case-test-cases-container");
    await renderWalletTests();
    updateTestSummaryElement();
}

function switchTab(tabName) {
    activeTab = tabName;

    var buttons = document.querySelectorAll(".tab-btn");
    for (var i = 0; i < buttons.length; i++) {
        if (buttons[i].getAttribute("data-tab") === tabName) {
            buttons[i].className = "tab-btn active";
        } else if (!buttons[i].classList.contains("disabled")) {
            buttons[i].className = "tab-btn";
        }
    }

    var contents = document.querySelectorAll(".tab-content");
    for (var j = 0; j < contents.length; j++) {
        if (contents[j].id === "tab-" + tabName) {
            contents[j].className = "tab-content active";
        } else {
            contents[j].className = "tab-content";
        }
    }
}

function createWalletTestCaseRow(testCase) {
    var row = document.createElement("div");
    row.className = "test-row wallet-test-row";
    row.id = "wallet-test-row-" + testCase.index;

    var hasWalletPrint = testCase.mode !== "grip";
    var hasGripPrint = testCase.mode !== "wallet";

    var refName = buildRefFilename("wallet", testCase).replace(".png", "");

    row.innerHTML =
        "<div class=\"test-label\">" +
            "<div class=\"test-label-name\">" + refName + "</div>" +
        "</div>" +
        "<div class=\"canvas-pair\">" +
            "<div class=\"canvas-wrapper\">" +
                "<div class=\"canvas-title\">Preview</div>" +
                "<div class=\"preview-container\">" +
                    "<div class=\"loading-placeholder\">Waiting...</div>" +
                "</div>" +
                "<div class=\"canvas-dims preview-dims\"></div>" +
            "</div>" +
            "<div class=\"canvas-wrapper\">" +
                "<div class=\"canvas-title\">Wallet Print</div>" +
                "<div class=\"wallet-print-container\">" +
                    (hasWalletPrint ? "<div class=\"loading-placeholder\">Waiting...</div>" : "<div class=\"na-placeholder\">N/A</div>") +
                "</div>" +
                "<div class=\"canvas-dims wallet-print-dims\"></div>" +
            "</div>" +
            "<div class=\"canvas-wrapper\">" +
                "<div class=\"canvas-title\">Grip Print</div>" +
                "<div class=\"grip-print-container\">" +
                    (hasGripPrint ? "<div class=\"loading-placeholder\">Waiting...</div>" : "<div class=\"na-placeholder\">N/A</div>") +
                "</div>" +
                "<div class=\"canvas-dims grip-print-dims\"></div>" +
            "</div>" +
        "</div>";

    return row;
}

async function renderWalletTestCase(testCase, container) {
    var walletDevice = stockAssets.walletDevice;
    var walletGripDevice = stockAssets.walletGripDevice;
    var walletOverlayID = mod.getOverlayImageID(walletDevice);

    var hasWalletPrint = testCase.mode !== "grip";
    var hasGripPrint = testCase.mode !== "wallet";

    // -- Preview Canvas --
    var previewWrapper = container.querySelector(".preview-container");
    previewWrapper.innerHTML = "";

    var previewDesign = buildCYODesign(testCase);
    previewDesign.walletPrintSurface = testCase.mode;
    previewDesign.stickerImages = testCase.stickers ? stockAssets.stickerImages : [];

    var compositor = await mod.Compositor.createInContainer(
        previewWrapper, walletDevice, previewDesign, null, walletDevice.imageID, walletOverlayID
    );

    // Create altCompositor for grip content on wallet preview (needed for split/both/grip modes)
    var altCompositor = null;
    if (testCase.mode !== "wallet" && walletGripDevice) {
        var altContainer = document.createElement("div");
        altCompositor = await mod.Compositor.createInContainer(
            altContainer, walletGripDevice, previewDesign, null, walletGripDevice.imageID, null,
            { isAltCompositor: true }
        );
        altCompositor.updateCompositeView(null);
    }
    compositor.updateCompositeView(null, altCompositor);

    var previewCanvas = previewWrapper.querySelector(".composite-canvas");
    var previewDims = container.querySelector(".preview-dims");
    if (previewCanvas && previewDims) {
        previewDims.textContent = previewCanvas.width + " x " + previewCanvas.height;
    }

    // Clear imageStorage
    mod.imageStorage.setUploadedImage(null);
    mod.imageStorage.setTransparentImage(null);

    if (testCase.userImage) {
        var walletImg = testCase.userImageKey === "testRect" ? stockAssets.testRectImage : stockAssets.userImage;
        var walletTransImg = testCase.userImageKey === "testRect" ? stockAssets.testRectImage : stockAssets.userTransparentImage;
        mod.imageStorage.setUploadedImage(walletImg);
        mod.imageStorage.setTransparentImage(walletTransImg);
    }

    mod.Product.device = walletDevice;
    var mockPreviewCanvas = { compositor: compositor, altCompositor: altCompositor };

    // -- Wallet Print Canvas --
    if (hasWalletPrint) {
        var walletPrintContainer = container.querySelector(".wallet-print-container");
        walletPrintContainer.innerHTML = "";

        var walletCanvas = await mod.CartUtils.generatePrintReadyCanvas(previewDesign, mockPreviewCanvas);
        walletCanvas.dataset.refName = buildRefFilename("wallet", testCase);

        walletCanvas.style.maxWidth = "280px";
        walletCanvas.style.height = "auto";
        walletCanvas.style.display = "block";
        walletCanvas.style.border = "1px solid #ccc";
        walletPrintContainer.appendChild(walletCanvas);

        var walletDims = container.querySelector(".wallet-print-dims");
        if (walletDims) {
            walletDims.textContent = walletCanvas.width + " x " + walletCanvas.height;
        }
    }

    // -- Grip Print Canvas --
    if (hasGripPrint) {
        var gripPrintContainer = container.querySelector(".grip-print-container");
        gripPrintContainer.innerHTML = "";

        var gripCanvas;
        if (testCase.mode === "both") {
            gripCanvas = await mod.CartUtils.generateBothModeGripPrintCanvas(previewDesign, mockPreviewCanvas);
        } else {
            gripCanvas = await mod.CartUtils.generateGripOnlyPrintCanvas(previewDesign, mockPreviewCanvas);
        }
        gripCanvas.dataset.refName = buildRefFilename("wallet", testCase, "grip");

        gripCanvas.style.maxWidth = "280px";
        gripCanvas.style.height = "auto";
        gripCanvas.style.display = "block";
        gripCanvas.style.border = "1px solid #ccc";
        gripPrintContainer.appendChild(gripCanvas);

        var gripDims = container.querySelector(".grip-print-dims");
        if (gripDims) {
            gripDims.textContent = gripCanvas.width + " x " + gripCanvas.height;
        }
    }

    // Clear imageStorage
    mod.imageStorage.setUploadedImage(null);
    mod.imageStorage.setTransparentImage(null);

    // -- Compare against reference bitmaps --
    var wrappers = container.querySelectorAll(".canvas-wrapper");
    var walletPrintWrapperEl = wrappers[1];
    var gripPrintWrapperEl = wrappers[2];

    var walletPrintRef = hasWalletPrint ? await loadReferenceImage(buildRefFilename("wallet", testCase)) : null;
    var gripPrintRef = hasGripPrint ? await loadReferenceImage(buildRefFilename("wallet", testCase, "grip")) : null;

    var walletPrintResult = null;
    if (hasWalletPrint) {
        var walletPrintCanvas = container.querySelector(".wallet-print-container canvas");
        if (walletPrintCanvas && walletPrintRef) {
            walletPrintResult = compareCanvasToReference(walletPrintCanvas, walletPrintRef, {});
            showComparisonResult(walletPrintWrapperEl, walletPrintResult, "280px");
        } else if (!walletPrintRef) {
            showNoReference(walletPrintWrapperEl);
        }
    }

    var gripPrintResult = null;
    if (hasGripPrint) {
        var gripPrintCanvas = container.querySelector(".grip-print-container canvas");
        if (gripPrintCanvas && gripPrintRef) {
            gripPrintResult = compareCanvasToReference(gripPrintCanvas, gripPrintRef, {});
            showComparisonResult(gripPrintWrapperEl, gripPrintResult, "280px");
        } else if (!gripPrintRef) {
            showNoReference(gripPrintWrapperEl);
        }
    }

    var walletResults = [];
    if (hasWalletPrint) {
        walletResults.push({ ref: walletPrintRef, result: walletPrintResult });
    }
    if (hasGripPrint) {
        walletResults.push({ ref: gripPrintRef, result: gripPrintResult });
    }
    updateTestRowStatus(container, walletResults);
}

async function renderWalletTests() {
    if (!stockAssets.walletDevice) {
        setStatus("No wallet device available.", true);
        return;
    }
    if (!stockAssets.walletGripDevice) {
        setStatus("No wallet grip device available (grip-poptop needed for wallet grip prints).", true);
        return;
    }

    var container = document.getElementById("wallet-test-cases-container");
    container.innerHTML = "";

    var sectionOrder = ["split", "both", "wallet", "grip"];
    var sectionLabels = { split: "Surface Mode: Split", both: "Surface Mode: Both", wallet: "Surface Mode: Wallet Only", grip: "Surface Mode: Grip Only" };

    // Group test cases by mode
    var grouped = {};
    for (var i = 0; i < WALLET_TEST_CASES.length; i++) {
        var mode = WALLET_TEST_CASES[i].mode;
        if (!grouped[mode]) grouped[mode] = [];
        grouped[mode].push(WALLET_TEST_CASES[i]);
    }

    // Build rows grouped by section
    var globalIndex = 0;
    for (var s = 0; s < sectionOrder.length; s++) {
        var sectionMode = sectionOrder[s];
        var cases = grouped[sectionMode];
        if (!cases || cases.length === 0) continue;

        var header = document.createElement("div");
        header.className = "section-header";
        header.textContent = sectionLabels[sectionMode];
        container.appendChild(header);

        for (var k = 0; k < cases.length; k++) {
            cases[k].index = globalIndex;
            container.appendChild(createWalletTestCaseRow(cases[k]));
            globalIndex++;
        }
    }

    // Render all test cases
    var allCases = [];
    for (var s2 = 0; s2 < sectionOrder.length; s2++) {
        var sectionCases = grouped[sectionOrder[s2]];
        if (sectionCases) {
            for (var c = 0; c < sectionCases.length; c++) {
                allCases.push(sectionCases[c]);
            }
        }
    }

    for (var j = 0; j < allCases.length; j++) {
        var testCase = allCases[j];
        var row = document.getElementById("wallet-test-row-" + testCase.index);
        setStatus("Rendering wallet test " + (j + 1) + "/" + allCases.length + "...");
        try {
            await renderWalletTestCase(testCase, row);
        } catch (err) {
            console.error("Error rendering wallet test case " + (j + 1) + ":", err);
            var walletPrintContainer = row.querySelector(".wallet-print-container");
            if (walletPrintContainer) {
                walletPrintContainer.innerHTML = "<div style=\"color: #d32f2f; padding: 10px;\">Error: " + err.message + "</div>";
            }
        }
    }

    renderedTabs["wallet"] = true;
    setStatus("All " + allCases.length + " wallet test cases rendered.");
}

async function initialize() {
    // Restore last-used CDN URL
    var savedCdnUrl = localStorage.getItem(STORAGE_KEY_CDN_URL);
    if (savedCdnUrl) {
        document.getElementById("cdn-url").value = savedCdnUrl;
    }

    // Tab switching
    var tabButtons = document.querySelectorAll(".tab-btn");
    for (var t = 0; t < tabButtons.length; t++) {
        tabButtons[t].addEventListener("click", function(event) {
            var tabName = event.currentTarget.getAttribute("data-tab");
            if (event.currentTarget.classList.contains("disabled")) return;
            switchTab(tabName);
            // Render tab if not already rendered
            if (!renderedTabs[tabName] && stockAssets.gripDevice) {
                renderActiveTab();
            }
        });
    }

    // CDN Load button
    document.getElementById("load-cdn-btn").addEventListener("click", async function() {
        var url = document.getElementById("cdn-url").value.trim();
        if (!url) {
            setStatus("Enter a CDN URL first.", true);
            return;
        }
        try {
            document.getElementById("load-cdn-btn").disabled = true;
            await loadFromCDN(url);
            localStorage.setItem(STORAGE_KEY_CDN_URL, url);
        } catch (err) {
            setStatus("CDN load failed: " + err.message, true);
            console.error("CDN load error:", err);
        } finally {
            document.getElementById("load-cdn-btn").disabled = false;
        }
    });

    // Render button - re-renders all tabs
    document.getElementById("render-btn").addEventListener("click", async function() {
        try {
            document.getElementById("render-btn").disabled = true;
            await initFeeds();
            await loadStockAssets();
            await renderAllTabs();
        } catch (err) {
            setStatus("Render failed: " + err.message, true);
            console.error("Render error:", err);
        } finally {
            document.getElementById("render-btn").disabled = false;
        }
    });

    // Save Reference Bitmaps button
    document.getElementById("save-refs-btn").addEventListener("click", async function() {
        var rows = document.querySelectorAll(".test-row");
        var delay = 100;

        for (var i = 0; i < rows.length; i++) {
            var row = rows[i];
            var canvases = row.querySelectorAll("canvas[data-ref-name]");
            for (var j = 0; j < canvases.length; j++) {
                downloadCanvasAsPNG(canvases[j], canvases[j].dataset.refName);
                await new Promise(function(r) { setTimeout(r, delay); });
            }
        }

        setStatus("Reference bitmaps saved. Move downloaded PNGs to print-preview-test/reference-bitmaps/");
    });

    // Auto-render on load
    try {
        await initFeeds();
        await loadStockAssets();
        await renderAllTabs();
    } catch (err) {
        setStatus("Initialization failed: " + err.message, true);
        console.error("Init error:", err);
    }
}

window.addEventListener("DOMContentLoaded", initialize);
